import request from '@oskecare/fe-utilities/lib/request';
import { OFFER_API } from '@oskecare/fe-utilities/lib/constants/dataSources';

/**
 * Ost library to handle requests to OST.
 *
 * @property {string} _ostApiUrl
 * @property {string} _apiKey
 * @property {Logger} _logger
 * @property {CustomerDataManager} _customerDataManager
 * @property {Products} _products
 */
export default class OstRequest {
  /**
   * @param {object} config
   * @param {Logger} logger
   * @param {CustomerDataManager} customerDataManager
   * @param {Products} products
   */
  constructor({ config, logger, customerDataManager, products }) {
    this._ostApiUrl = config.ostApiUrl;
    this._apiKey = config.apiKey;
    this._logger = logger;
    this._customerDataManager = customerDataManager;
    this._products = products;
  }

  /**
   * Returns result of OST `get-init-with-catalogue` operation.
   * Flag readOnly set to false will prevent caching and saving to DB in OST API.
   * Catalog from this endpoint will contain `doNotOffer` services, that user have active.
   *
   * @param {string} subscriptionId
   * @param {boolean} readOnly
   * @returns {Promise}
   */
  getInitWithCatalog(subscriptionId, readOnly = false) {
    return this._ostRequest({
      endpoint: 'get-init-with-catalogue',
      parameters: {
        x_user_id: subscriptionId,
        x_readonly: readOnly ? 1 : 0,
      },
    });
  }

  /**
   * Returns result of OST `init` operation.
   * Flag readOnly set to false will prevent caching and saving to DB in OST API.
   *
   * @param {string} subscriptionId
   * @param {boolean} readOnly
   * @returns {Promise}
   */
  init(subscriptionId, readOnly = false) {
    return this._ostRequest({
      endpoint: 'init',
      parameters: {
        x_user_id: subscriptionId,
        x_readonly: readOnly ? 1 : 0,
      },
    });
  }

  /**
   * Returns result of OST `update` endpoint for `add` operation.
   * Parameter `attributes` is optional and is `Map` of service attributes, where:
   * - Key is service attribute id.
   * - Value is service attribute value.
   *
   * @param {string} subscriptionId
   * @param {string} basketId
   * @param {number} itemId
   * @param {Map} attributes
   * @returns {Promise}
   */
  updateAdd({ subscriptionId, basketId, itemId, attributes = new Map() }) {
    return this._ostRequest({
      endpoint: 'update',
      parameters: {
        x_user_id: subscriptionId,
        x_item_operation: 'add',
        x_online_basket_id: basketId,
        x_item_id: itemId,
        attributes,
      },
    });
  }

  /**
   * Returns result of OST `update` endpoint for `remove` operation.
   *
   * @param {string} subscriptionId
   * @param {string} basketId
   * @param {number} itemCustomerId
   * @returns {Promise}
   */
  updateRemove({ subscriptionId, basketId, itemCustomerId }) {
    return this._ostRequest({
      endpoint: 'update',
      parameters: {
        x_user_id: subscriptionId,
        x_item_operation: 'remove',
        x_online_basket_id: basketId,
        x_item_customer_order_id: itemCustomerId,
      },
    });
  }

  /**
   * Returns result of OST `update` endpoint for `update` operation.
   * Parameter `attributes` is `Map` of service attributes, where:
   * - Key is service attribute id.
   * - Value is service attribute value.
   *
   * @param {string} subscriptionId
   * @param {string} basketId
   * @param {number} itemCustomerId
   * @param {Map} attributes
   * @returns {Promise}
   */
  updateUpdate({ subscriptionId, basketId, itemCustomerId, attributes }) {
    return this._ostRequest({
      endpoint: 'update',
      parameters: {
        x_user_id: subscriptionId,
        x_item_operation: 'update',
        x_online_basket_id: basketId,
        x_item_customer_order_id: itemCustomerId,
        attributes,
      },
    });
  }

  /**
   * Loads data for existing Prima Kids prepaid profile.
   *
   * @param {string} subscriptionId
   * @param {string} basketId
   * @param {string} primaKidsSn
   * @returns {Promise}
   */
  updateLoadPrimaKids({ subscriptionId, basketId, primaKidsSn }) {
    return this._ostRequest({
      endpoint: 'update',
      parameters: {
        x_user_id: subscriptionId,
        x_item_operation: 'update',
        x_online_basket_id: basketId,
        x_prima_kids_sn: primaKidsSn,
      },
    });
  }

  /**
   * Returns result of OST `check-attribute-inputs` endpoint for `update` operation.
   * Parameter `attributesValues` is `Map`, where key is id of service attribute, where:
   * - Key is service attribute id.
   * - Value is service attribute value.
   *
   * OST endpoint accepts attribute values as query parameters.
   * We need to transform Map to key-value object.
   *
   * All attributes, that are user inputs (for now all of them) are sent as duplicates
   * with different key prefix.
   *
   * @param {string} subscriptionId
   * @param {number} serviceUuid
   * @param {Map} attributesValues
   * @param {?string} primaKidsSn
   * @returns {Promise}
   */
  checkAttributeInputs(subscriptionId, serviceUuid, attributesValues, primaKidsSn = null) {
    const keyValueObj = {};
    attributesValues.forEach((value, key) => {
      keyValueObj[`x_other_input_attribute_${key}`] = value;
      keyValueObj[key] = value;
    });

    return this._ostRequest({
      endpoint: 'check-attribute-inputs',
      parameters: {
        x_user_id: subscriptionId,
        x_uuid: serviceUuid,
        x_prima_kids_sn: primaKidsSn,
        ...keyValueObj,
      },
    });
  }

  /**
   * Returns result of OST `submit-order` operation
   *
   * @param {string} subscriptionId
   * @param {string} basketId
   * @returns {Promise}
   */
  submitOrder(subscriptionId, basketId) {
    return this._ostRequest({
      endpoint: 'submit-order',
      parameters: {
        x_user_id: subscriptionId,
        x_online_basket_id: basketId,
      },
      isImportant: true,
    }).then(data => {
      this._products.resetCache();
      return data;
    });
  }

  /**
   * Returns result of OST `get-order-summary` operation
   *
   * @param {string} subscriptionId
   * @param {string} basketId
   * @returns {Promise}
   */
  orderSummary(subscriptionId, basketId) {
    return this._ostRequest({
      endpoint: 'get-order-summary',
      parameters: {
        x_user_id: subscriptionId,
        x_online_basket_id: basketId,
      },
    });
  }

  /**
   * Returns result of OST `is-supported` operation.
   * Checks, if `subscriptionId` is supported by OST and can use OST services.
   *
   * @param {string} subscriptionId
   * @returns {Promise<{isSupported: "Y"|"N", reason: "OLD"|"NEW"|"FUTURE"}>}
   */
  async isSupported(subscriptionId) {
    return this._ostRequest({
      endpoint: 'is-supported',
      parameters: {
        x_user_id: subscriptionId,
      },
      getCustomLogMessage: response =>
        `; isSupported: ${response.responseData.isSupported}, reason: ${response.responseData.reason}`,
    });
  }

  /**
   * Returns information about failed love order, if any is present.
   *
   * @param {string} subscriptionId
   * @returns {Promise}
   */
  failedLove(subscriptionId) {
    return this._ostRequest({
      endpoint: 'get-failed-love',
      parameters: {
        x_user_id: subscriptionId,
      },
    });
  }

  /**
   * Returns information about in progress love order, if any is present.
   *
   * @param {string} subscriptionId
   * @returns {Promise}
   */
  semifinishedLove(subscriptionId) {
    return this._ostRequest({
      endpoint: 'get-semifinished-love',
      parameters: {
        x_user_id: subscriptionId,
      },
    });
  }

  /**
   * @param {string} subscriptionId
   * @param {{voice_sn: string, fix_sn: string}[]} members
   * @returns {Promise}
   */
  postNewLoveGroups({ subscriptionId, members }) {
    return this._ostRequest({
      endpoint: 'submit-love',
      method: 'POST',
      body: {
        x_user_id: subscriptionId,
        x_love_type: 'KONV_LOVE_INET',
        members,
      },
      isImportant: true,
    });
  }

  /**
   * This is temporary method.
   *
   * Makes request to OST `ping` endpoint to ensure NODESESSIONID cookie
   * is correctly set and used for all following calls.
   *
   * Should be called only once before any other OST requests.
   *
   * `ping` endpoint is used without any parameters
   * and without expecting success/fail of the call.
   * @returns {Promise<void>}
   */
  async setOstCookie() {
    try {
      await this._ostRequest({ endpoint: 'ping', skipLog: true });
    } catch {
      // ignore thrown error
    }
  }

  /**
   * Calls OST endpoint with given parameters and returns responseData.
   * Parameter `parameters` can contain Map, which will be translated into query array.
   *
   * @param {string} method
   * @param {string} endpoint
   * @param {object} body
   * @param {object} parameters
   * @param {boolean} isImportant
   * @param {boolean} skipLog
   * @param {?function(object): string} getCustomLogMessage
   * @returns {Promise<object>}
   */
  async _ostRequest({
    method = 'GET',
    endpoint,
    body = null,
    parameters = {},
    isImportant = false,
    skipLog = false,
    getCustomLogMessage = () => '',
  }) {
    const route = `${this._ostApiUrl}/${endpoint}`;
    const query = OstRequest.getQueryFromParameters(parameters);

    const { isCustomerAdmin } = (await this._customerDataManager.getCustomerData()).cnData;

    return request({
      method,
      route,
      query,
      body,
      headers: {
        Apikey: this._apiKey,
      },
      logSource: 'infra-ost',
      dataSource: OFFER_API,
      getIsValidResponse: response => response?.success === '1',
      getIsExpectedErrorResponse: response =>
        response?.errorFlag === 'insufficient_rights_id_code_verification' && !isCustomerAdmin,
      isImportant,
    })
      .then(response => {
        if (!skipLog) {
          const queryString = new URLSearchParams(query).toString();
          const customLog = getCustomLogMessage(response);
          this._logger.info(
            `OST ${method} ${route}${queryString ? `?${queryString}` : ''}${response.transactionId ? `; TID ${response.transactionId}` : ''}${customLog}`,
            'infra-ost',
          );
        }
        return response.responseData;
      })
      .catch(this._logger.catchError);
  }

  /**
   * @param {object} parameters
   * @returns {object}
   */
  static getQueryFromParameters(parameters = {}) {
    const query = {};
    const keys = Object.keys(parameters);
    if (keys) {
      keys.forEach(key => {
        if (parameters[key] instanceof Map) {
          query[key] = [];
          parameters[key].forEach((attributeValue, attributeName) => {
            query[key].push(`{"${attributeName}":"${attributeValue}"}`);
          });
        } else {
          query[key] = parameters[key];
        }
      });
    }
    return query;
  }
}
